import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Search, Loader2, Sparkles, Mic, MicOff, X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { showAIErrorToast } from "@/lib/aiErrorHandler";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

export interface SearchResult extends Document {
  matchScore?: number;
  matchReason?: string;
}

// Typewriter hook
function useTypewriter(texts: string[], typingSpeed = 80, pauseDuration = 2000) {
  const [displayText, setDisplayText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!texts.length) return;
    const currentText = texts[textIndex] || "";
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < currentText.length) {
          setDisplayText(currentText.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        if (charIndex > 0) {
          setDisplayText(currentText.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setIsDeleting(false);
          setTextIndex((textIndex + 1) % texts.length);
        }
      }
    }, isDeleting ? typingSpeed / 2 : typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, texts, typingSpeed, pauseDuration]);

  return displayText;
}

interface SearchState {
  isSearching: boolean;
  searchQuery: string;
  searchResults: SearchResult[];
  isLoading: boolean;
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

interface PremiumAISearchProps {
  onDocumentClick?: (doc: Document) => void;
  onSearchStateChange?: (state: SearchState) => void;
}

export function PremiumAISearch({ onDocumentClick, onSearchStateChange }: PremiumAISearchProps) {
  const { user } = useAuth();
  const { t, lang } = useTranslation();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const { checkLimit, incrementUsage } = useUsageLimits();

  const placeholderExamples = Array.isArray((t as any)?.dashboard?.placeholders)
    ? ((t as any).dashboard.placeholders as string[])
    : [];
  const typingPlaceholder = useTypewriter(
    placeholderExamples.length ? placeholderExamples : [lang === "en" ? "Search…" : "Rechercher…"]
  );

  // Load more results function
  const loadMoreResults = async () => {
    if (!searchQuery.trim() || !user?.id || isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    const newOffset = currentOffset + 20;
    
    try {
      const { data, error } = await supabase.functions.invoke("search-documents", {
        body: { query: searchQuery.trim(), userId: user.id, offset: newOffset },
      });
      
      if (error) throw error;
      
      if (data?.results) {
        setSearchResults(prev => [...prev, ...data.results]);
        setCurrentOffset(newOffset);
        setHasMore(data.hasMore ?? false);
        setTotalCount(data.totalCount ?? searchResults.length + data.results.length);
      }
    } catch (err: any) {
      showAIErrorToast(err, "le chargement des résultats");
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Notify parent of search state changes
  useEffect(() => {
    onSearchStateChange?.({
      isSearching: isSearchActive,
      searchQuery,
      searchResults,
      isLoading: searchLoading,
      totalCount,
      hasMore,
      isLoadingMore,
      loadMore: loadMoreResults,
    });
  }, [isSearchActive, searchQuery, searchResults, searchLoading, totalCount, hasMore, isLoadingMore]);

  // Cmd+K shortcut to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.getElementById("ai-search-input-field")?.focus();
      }
      // Escape to clear search
      if (e.key === "Escape" && isSearchActive) {
        clearSearch();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSearchActive]);

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchActive(false);
    setTotalCount(0);
    setHasMore(false);
    setCurrentOffset(0);
  };

  const startVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error(t?.dashboard?.toasts?.voiceNotSupported || "Voice not supported");
      return;
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'en' ? 'en-US' : 'fr-FR';
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event: any) => {
      const transcript = event?.results?.[0]?.[0]?.transcript || "";
      setSearchQuery(transcript);
      setIsListening(false);
      if (transcript) {
        handleAiSearch(transcript);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const handleAiSearch = async (query: string) => {
    if (!query.trim() || !user?.id) return;
    if (!checkLimit("ai_searches")) {
      toast.error(t?.dashboard?.toasts?.aiLimitReached || "AI search limit reached");
      return;
    }
    
    setIsSearchActive(true);
    setSearchLoading(true);
    setSearchResults([]);
    setCurrentOffset(0);
    setTotalCount(0);
    setHasMore(false);

    try {
      const { data, error } = await supabase.functions.invoke("search-documents", {
        body: { query: query.trim(), userId: user.id, offset: 0 },
      });
      if (error) {
        const errorStatus = (error as any)?.status;
        if (errorStatus === 429 || errorStatus === 402) {
          showAIErrorToast({ status: errorStatus }, "la recherche IA");
          return;
        }
        throw error;
      }
      if (data?.results) {
        setSearchResults(data.results);
        setTotalCount(data.totalCount ?? data.results.length);
        setHasMore(data.hasMore ?? false);
        await incrementUsage("ai_searches");
      }
    } catch (err: any) {
      showAIErrorToast(err, "la recherche IA");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = () => {
    if (searchQuery.trim()) {
      handleAiSearch(searchQuery);
    }
  };

  return (
    <div className="mb-6">
      {/* AI Search Bar */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div 
          id="ai-search-input" 
          className="relative bg-gradient-to-r from-primary/5 via-card to-primary/5 rounded-2xl border border-primary/20 p-1.5 shadow-lg shadow-primary/5"
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/10 via-transparent to-primary/10 blur-xl opacity-50 pointer-events-none" />
          
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10">
                <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              </div>
              <Input
                id="ai-search-input-field"
                placeholder={typingPlaceholder || "Rechercher..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim()) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                className="pl-12 pr-20 h-12 md:h-14 text-base border-0 bg-card/80 backdrop-blur-sm rounded-xl focus-visible:ring-2 focus-visible:ring-primary/30 placeholder:text-muted-foreground/60"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {/* Clear button when searching */}
                {isSearchActive && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:h-9 md:w-9 rounded-lg text-muted-foreground hover:text-foreground"
                    onClick={clearSearch}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 md:h-9 md:w-9 rounded-lg ${isListening ? 'bg-destructive/10 text-destructive animate-pulse' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                  onClick={startVoiceRecognition}
                  disabled={isListening}
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                {/* Keyboard shortcut hint */}
                {!isSearchActive && (
                  <kbd className="hidden md:flex items-center gap-1 px-2 py-1 rounded bg-muted border border-border text-[10px] text-muted-foreground">
                    ⌘K
                  </kbd>
                )}
              </div>
            </div>
            <Button
              size="lg"
              className="h-12 md:h-14 px-4 md:px-6 shrink-0 rounded-xl bg-primary hover:bg-primary/90 shadow-md shadow-primary/20"
              onClick={handleSubmit}
              disabled={searchLoading}
            >
              {searchLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Suggestions - hide when searching */}
        {!isSearchActive && (
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {lang === 'en' ? 'Try:' : 'Essayez :'}
            </span>
            {placeholderExamples.slice(0, 4).map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setSearchQuery(suggestion);
                  handleAiSearch(suggestion);
                }}
                className="text-[10px] px-2 py-1 rounded-full bg-card border border-border text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-all whitespace-nowrap"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}